import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleWhatIsGTBuy } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuy.run";
const articlePath = "/article/what-is-gtbuy-spreadsheet";

export default function ArticlePage() {
  const article = articleWhatIsGTBuy;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.title,
            "description": article.excerpt,
            "author": {
              "@type": "Person",
              "name": article.author
            },
            "datePublished": article.date,
            "dateModified": article.date,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${siteUrl}${articlePath}`
            },
            "url": `${siteUrl}${articlePath}`,
            "image": "https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20with%20laptop%20displaying%20spreadsheet%2C%20warm%20cream%20neutral%20aesthetic%2C%20soft%20natural%20daylight%20from%20window%2C%20cozy%20textured%20linen%20fabric%2C%20editorial%20productivity%20scene%2C%20clean%20composition%2C%20high%20detail&width=1200&height=630&seq=article-seo-gtbuy&orientation=landscape",
            "publisher": {
              "@type": "Organization",
              "name": "GTBuy Spreadsheet",
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg"
              }
            }
          })
        }}
      />
      <Navbar />
      <main>
        <article>
          <header className="relative pt-28 pb-16 md:pt-36 md:pb-20 px-6 md:px-10 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 items-start">
                <div className="flex-1">
                  <p className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-800 text-xs font-medium mb-5">
                    Buying Guide
                  </p>
                  <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                    {article.title}
                  </h1>
                  <p className="mt-5 text-foreground-600 leading-relaxed max-w-2xl">
                    {article.excerpt}
                  </p>
                </div>
                <div className="flex-shrink-0 flex flex-col items-start gap-4">
                  <div className="text-sm text-foreground-500">
                    <p className="font-medium text-foreground-700">{article.author}</p>
                    <p>{article.date}</p>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center px-4 py-2 rounded-full border-2 border-primary-500 text-primary-700 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-primary-500 hover:text-background-50 transition-colors"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Back to GTBuy Home
                  </Link>
                </div>
              </div>
            </div>
          </header>

          <div className="py-12 md:py-16 px-6 md:px-10 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-16">
                <div className="lg:w-[65%] space-y-8">
                  {article.sections.map((section, idx) => {
                    if (section.type === "paragraph") {
                      return (
                        <p key={idx} className="text-foreground-700 leading-relaxed text-base">
                          {section.content}
                        </p>
                      );
                    }
                    if (section.type === "heading") {
                      return (
                        <h2 key={idx} className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 pt-4">
                          {section.content}
                        </h2>
                      );
                    }
                    if (section.type === "table" && "headers" in section && "rows" in section) {
                      return (
                        <div key={idx} className="overflow-x-auto rounded-xl border border-background-200/70">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-primary-700 text-background-50">
                                {section.headers.map((h: string, hi: number) => (
                                  <th key={hi} className="text-left px-5 py-3.5 font-semibold whitespace-nowrap">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {section.rows.map((row: string[], ri: number) => (
                                <tr key={ri} className={ri % 2 === 0 ? "bg-background-50" : "bg-background-100"}>
                                  {row.map((cell: string, ci: number) => (
                                    <td key={ci} className="px-5 py-3 text-foreground-600 text-xs leading-relaxed">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    if (section.type === "faq" && "items" in section) {
                      return (
                        <div key={idx} className="space-y-5">
                          {section.items.map((item: { question: string; answer: string }, fi: number) => (
                            <div key={fi} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                              <h3 className="font-semibold text-foreground-900 text-sm flex items-start gap-2">
                                <i className="ri-question-answer-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                                {item.question}
                              </h3>
                              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                                {item.answer}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })}

                  <div className="flex flex-col sm:flex-row gap-3 pt-8">
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Back to GTBuy Home
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center px-7 py-3 rounded-full border-2 border-primary-500 text-primary-700 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-500 hover:text-background-50 transition-colors"
                    >
                      Visit GTBuy Sheet
                      <i className="ri-arrow-right-up-line ml-2"></i>
                    </a>
                  </div>
                </div>

                <div className="lg:w-[35%]">
                  <div className="sticky top-24 space-y-8">
                    <div className="rounded-2xl overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20photography%20with%20a%20laptop%20displaying%20a%20spreadsheet%20on%20screen%2C%20cream%20and%20warm%20neutral%20aesthetic%2C%20soft%20natural%20daylight%20from%20window%2C%20cozy%20textured%20linen%20fabric%2C%20ceramic%20coffee%20mug%2C%20editorial%20productivity%20scene%2C%20clean%20composition%2C%20high%20detail&width=500&height=700&seq=article-sidebar-gtbuy&orientation=portrait"
                        alt="GTBuy Spreadsheet workspace guide"
                        className="w-full rounded-2xl"
                      />
                    </div>

                    <div className="p-6 rounded-2xl bg-accent-100/50 border border-accent-200/50">
                      <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                        Start Shopping Smarter
                      </h4>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                        Ready to put this knowledge into practice? Visit the main GTBuy platform to start comparing products across categories.
                      </p>
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
                      >
                        Go to GTBuy Sheet
                        <i className="ri-arrow-right-up-line ml-1"></i>
                      </a>
                    </div>

                    <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                      <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                        Article Summary
                      </h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        This guide covers everything you need to know about the GTBuy Spreadsheet system — from basic concepts to advanced comparison techniques. Learn how structured data intelligence can transform your online shopping experience.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="py-16 md:py-24 px-6 md:px-10 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 text-center mb-12">
                Continue Reading
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Link
                  to="/"
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer bg-gradient-to-br from-primary-100 to-accent-100"
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50 mb-4">
                      <i className="ri-home-4-line text-primary-600 text-xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900">
                      Back to Homepage
                    </h3>
                    <p className="text-sm text-foreground-500 mt-2">
                      Return to the main GTBuy Spreadsheet hub with all categories and comparison tools.
                    </p>
                    <span className="mt-4 text-sm font-semibold text-primary-700 group-hover:underline whitespace-nowrap">
                      Go Home <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </div>
                </Link>

                {[
                  {
                    title: "GTBuy vs Kakobuy Spreadsheet",
                    desc: "Full 2026 comparison of features, data depth, and automation capabilities between the two systems.",
                  },
                  {
                    title: "Spreadsheet Shopping Future",
                    desc: "Discover why structured data intelligence is replacing manual browsing in modern e-commerce.",
                  },
                ].map((card, i) => (
                  <div key={i} className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-secondary-100 to-background-200">
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/80 mb-4">
                        <i className="ri-article-line text-secondary-600 text-xl"></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900">
                        {card.title}
                      </h3>
                      <p className="text-sm text-foreground-500 mt-2">
                        {card.desc}
                      </p>
                      <span className="mt-4 inline-block px-3 py-1 rounded-full bg-secondary-200 text-secondary-700 text-xs font-medium whitespace-nowrap">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-24 px-6 md:px-10 bg-background-50">
            <div className="max-w-4xl mx-auto text-center">
              <p className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                Start Today
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
                Start spreadsheet-based
                <br />
                shopping today
              </h2>
              <p className="mt-5 text-foreground-600 max-w-lg mx-auto leading-relaxed">
                Apply the GTBuy Spreadsheet methodology to your next purchase and experience the difference data-driven shopping makes.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center mt-8 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                Start Smart Shopping
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}